import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { EQM1030_R01ChartData } from './Dto_EQM1030_R01';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface ChartProps {
    data: EQM1030_R01ChartData[];
}

export const Chart_EQM1030_R01: React.FC<ChartProps> = ({ data }) => {
    // 차트 데이터 변환
    const chartConfigs = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        // displaySeq 기준으로 그룹핑 (동일 공정/항목 묶음)
        const seqToItems = new Map<number, EQM1030_R01ChartData[]>();
        data.forEach((item) => {
            const seq = (item.displaySeq ?? 0) as number;
            if (!seqToItems.has(seq)) seqToItems.set(seq, []);
            seqToItems.get(seq)!.push(item);
        });

        // 시간 라벨 (7:00 ~ 21:00, 10분 단위)
        const labels: string[] = [];
        for (let h = 7; h <= 21; h++) {
            for (let m = 0; m < 60; m += 10) {
                labels.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
            }
        }

        // 개별 아이템을 시계열 값으로 변환하는 헬퍼
        const toSeries = (chartItem: EQM1030_R01ChartData) => {
            const minuteGroups = new Map<string, { values: number[]; times: string[] }>();
            chartItem.data.forEach((point) => {
                const time = point.logMinute;
                let hour = '';
                let minute = '';
                if (time.includes(' ')) {
                    const t = time.split(' ')[1];
                    hour = t.split(':')[0];
                    minute = t.split(':')[1];
                } else if (time.includes(':')) {
                    hour = time.split(':')[0];
                    minute = time.split(':')[1];
                } else {
                    hour = time;
                    minute = '00';
                }
                const minuteGroup = Math.floor(parseInt(minute, 10) / 10) * 10;
                const key = `${hour.padStart(2, '0')}:${minuteGroup.toString().padStart(2, '0')}`;
                if (!minuteGroups.has(key)) minuteGroups.set(key, { values: [], times: [] });
                const rec = minuteGroups.get(key)!;
                const numeric = (point as any).value ?? (point as any).settingValue ?? null;
                if (numeric !== null && typeof numeric === 'number' && !Number.isNaN(numeric)) {
                    rec.values.push(numeric);
                }
                rec.times.push(time);
            });
            const series: (number | null)[] = [];
            const timeAt: (string | null)[] = [];
            labels.forEach(key => {
                const groupData = minuteGroups.get(key);
                if (!groupData || groupData.values.length === 0) {
                    series.push(null);
                    timeAt.push(null);
                } else {
                    const maxValue = Math.max(...groupData.values);
                    const maxIndex = groupData.values.indexOf(maxValue);
                    const maxTime = groupData.times[maxIndex];
                    series.push(maxValue);
                    timeAt.push(maxTime || null);
                }
            });
            return { series, timeAt };
        };

        const configs = Array.from(seqToItems.entries()).map(([seq, items]) => {
            // 대표 이름 구성: ref03 + ref04가 있으면 합쳐서 사용, 없으면 chartName에서 현재/설정 제거
            const pickWithMeta = items.find(i => (i.ref03 && i.ref03.trim()) || (i.ref04 && i.ref04.trim())) || items[0];
            const r3 = (pickWithMeta.ref03 || '').toString().trim();
            const r4 = (pickWithMeta.ref04 || '').toString().trim();
            const metaName = [r3, r4].filter(Boolean).join(' ');
            const baseName = metaName || (items[0]?.chartName || '').replace(/\s*(현재|설정)$/g, '');

            // 현재/설정 후보 탐색
            const currentItem =
                items.find(i => /현재/.test(i.chartName)) ||
                items.find(i => i.data.some(p => (p as any).value != null)) ||
                items[0];
            const settingItem =
                items.find(i => /설정/.test(i.chartName)) ||
                items.find(i => i.data.some(p => (p as any).settingValue != null)) ||
                undefined;

            const curr = toSeries(currentItem);
            const sett = settingItem ? toSeries(settingItem) : { series: new Array(labels.length).fill(null), timeAt: new Array(labels.length).fill(null) };

            // Y축 범위 계산
            const flatValues = [...curr.series, ...sett.series].filter((v): v is number => v != null);
            const maxValue = flatValues.length ? Math.max(...flatValues) : 0;
            const minValue = flatValues.length ? Math.min(...flatValues) : 0;
            const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 0;
            const yMin = minValue > 0 ? Math.floor(minValue * 0.9) : 0;

            const chartData = {
                labels,
                datasets: [
                    {
                        label: `${baseName} (현재)`,
                        data: curr.series,
                        borderColor: 'rgb(251, 146, 60)', // Orange
                        backgroundColor: 'rgba(251, 146, 60, 0.1)',
                        tension: 0.1,
                        pointRadius: 3, // 1.5x
                        pointHoverRadius: 6, // 1.5x
                        pointBackgroundColor: 'rgb(251, 146, 60)',
                        pointBorderColor: 'rgb(251, 146, 60)',
                        pointBorderWidth: 1.5, // 1.5x
                        fill: false,
                        // 커스텀 메타: 각 포인트의 실제 시간
                        metaTimes: curr.timeAt,
                    } as any,
                    {
                        label: `${baseName} (설정)`,
                        data: sett.series,
                        borderColor: 'rgb(59, 130, 246)', // Blue-500
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.1,
                        pointRadius: 3, // 1.5x
                        pointHoverRadius: 6, // 1.5x
                        pointBackgroundColor: 'rgb(59, 130, 246)',
                        pointBorderColor: 'rgb(59, 130, 246)',
                        pointBorderWidth: 1.5, // 1.5x
                        fill: false,
                        metaTimes: sett.timeAt,
                    } as any,
                ],
            };

            const chartOptions = {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    title: {
                        display: true,
                        text: baseName,
                        font: {
                            size: 30,
                            weight: 'bold' as const,
                        },
                    },
                    legend: {
                        position: 'top' as const,
                        labels: {
                            usePointStyle: false,
                            boxWidth: 40,
                            boxHeight: 3,
                            font: { size: 13 },
                        },
                    },
                    tooltip: {
                        enabled: true,
                        mode: 'index' as const,
                        intersect: false,
                        titleFont: { size: 13 },
                        bodyFont: { size: 12 },
                        callbacks: {
                            label: function(context: any) {
                                const value = context.parsed?.y;
                                const ds: any = context.dataset || {};
                                const t: string | null = ds.metaTimes?.[context.dataIndex] || null;
                                let timeDisplay = '';
                                if (t) {
                                    timeDisplay = t.includes(' ') ? t.split(' ')[1] : t; // HH:MM 또는 HH:MM:SS
                                }
                                const base = `${context.dataset.label}: ${Number(value).toFixed(1)}`;
                                return timeDisplay ? `${base} (${timeDisplay})` : base;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        ticks: { maxTicksLimit: 15, font: { size: 12 } },
                        title: { display: false, text: '' }
                    },
                    y: {
                        beginAtZero: false,
                        min: yMin,
                        max: yMax,
                        title: { display: true, text: '값', font: { size: 12 } },
                        ticks: { font: { size: 12 } },
                    },
                },
            };

            return {
                chartData,
                chartOptions,
                chartItem: { ...items[0] },
                maxTimeInfo: {},
            };
        });

        return configs;
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">차트 데이터가 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 h-full w-full border border-gray-300 ">
            <div className="flex-1 overflow-auto">
                <div className="grid grid-cols-4 gap-4">
                    {chartConfigs.map((config, index) => (
                        <div key={`chart-${config.chartItem.chartId}-${index}`} className="bg-white  shadow px-2">
                            <div className="h-[320px]">
                                <Line data={config.chartData} options={config.chartOptions} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

